<template>
  <v-card class="form-card" flat>
    <v-card-title class="pa-4 form-header d-flex align-center">
      <v-icon color="#023449" class="mr-2">mdi-file-document-edit</v-icon>
      <span class="form-title">Nova Consulta de Limite de Crédito</span>
      <v-spacer></v-spacer>
      <v-btn
          v-if="!mostrarFormulario"
          color="primary"
          class="criar-btn elevation-1"
          @click="mostrarFormulario = true"
          small
      >
        <v-icon left small>mdi-plus</v-icon>
        Criar Consulta
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text v-if="mostrarFormulario" class="pa-4">
      <v-form ref="form" v-model="valid">
        <v-row>
          <!-- Busca de Cliente -->
          <v-col cols="12" md="8">
            <v-autocomplete
                v-model="clienteSelecionado"
                :items="clientes"
                :loading="loadingClientes"
                :search-input.sync="searchCliente"
                item-text="nome"
                item-value="idcliente"
                label="Buscar Cliente"
                placeholder="Digite o nome ou código do cliente"
                prepend-inner-icon="mdi-account-search"
                clearable
                no-filter
                dense
                return-object
                outlined
                hide-details="auto"
                :rules="[v => !!v || 'Selecione um cliente']"
                @change="onClienteChange"
                class="busca-cliente"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-semibold item-nome">
                    <v-icon
                        x-small
                        :color="item.tipofisicajuridica === 'F' ? '#3b82f6' : '#059669'"
                        class="mr-2"
                    >
                      {{ item.tipofisicajuridica === 'F' ? 'mdi-account' : 'mdi-domain' }}
                    </v-icon>
                    {{ item.nome }}
                    <v-chip
                        v-if="item.idcolaborador > 0"
                        x-small
                        label
                        color="#f59e0b"
                        text-color="white"
                        class="ml-2 font-weight-bold"
                    >
                      <v-icon x-small left>mdi-badge-account</v-icon>
                      Colaborador
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption item-info">
                    <span class="codigo-cliente">Código: {{ item.idcliente }}</span>
                    <span class="separador">•</span>
                    <span class="documento-cliente">
            {{ item.cpfcnpj ? formatarCpfCnpj(item.cpfcnpj) : 'CPF/CNPJ não informado' }}
          </span>
                    <template v-if="item.idcolaborador > 0">
                      <span class="separador">•</span>
                    </template>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>

              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text-caption grey--text text-center py-2">
                      <v-icon small color="#94a3b8" class="mr-2">
                        {{ searchCliente ? 'mdi-account-off' : 'mdi-magnify' }}
                      </v-icon>
                      {{ searchCliente ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes' }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <template v-slot:append>
                <v-fade-transition leave-absolute>
                  <v-progress-circular
                      v-if="loadingClientes"
                      size="24"
                      width="2"
                      color="#023449"
                      indeterminate
                  ></v-progress-circular>
                </v-fade-transition>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Valor de Venda -->
          <v-col cols="12" md="4">
            <v-text-field
                v-model="valorVenda"
                label="Valor de Venda"
                placeholder="0,00"
                :prefix="valorVenda ? 'R$' : ''"
                prepend-inner-icon="mdi-cash"
                outlined
                dense
                hide-details="auto"
                :rules="[
                  v => !!v || 'Informe o valor',
                  v => parseFloat(v.replace('.', '').replace(',', '.')) > 0 || 'Valor deve ser maior que zero'
                ]"
                @input="formatarValor"
                @keypress="somenteNumeros"
                class="valor-input"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Loading Skeleton -->
        <div v-if="loadingClienteInfo" class="cliente-preview mt-3">
          <div class="preview-header-container mb-3">
            <v-skeleton-loader type="text" width="180" class="skeleton-custom"></v-skeleton-loader>
            <v-skeleton-loader type="chip" width="60" class="skeleton-custom"></v-skeleton-loader>
          </div>

          <v-divider class="mb-3"></v-divider>

          <v-row dense>
            <v-col cols="12">
              <v-skeleton-loader type="text" width="100%" class="skeleton-custom mb-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-skeleton-loader type="text" width="90%" class="skeleton-custom mb-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-skeleton-loader type="text" width="80%" class="skeleton-custom mb-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-skeleton-loader type="text" width="80%" class="skeleton-custom mb-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-skeleton-loader type="text" width="85%" class="skeleton-custom mb-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-skeleton-loader type="text" width="95%" class="skeleton-custom mb-2"></v-skeleton-loader>
            </v-col>
            <v-col cols="12">
              <v-skeleton-loader type="text" width="100%" class="skeleton-custom"></v-skeleton-loader>
            </v-col>
          </v-row>
        </div>

        <!-- Pré-visualização dos Dados do Cliente -->
        <v-expand-transition>
          <div v-if="clienteSelecionado && !loadingClienteInfo" class="cliente-preview mt-3">
            <!-- Header com Badge -->
            <div class="preview-header-container mb-3">
              <div class="preview-header">
                <v-icon small color="#023449" class="mr-2">mdi-card-account-details</v-icon>
                <span class="preview-title">Informações do Cliente - CISSPoder</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px;">
                <v-chip
                    v-if="clienteSelecionado.idcolaborador > 0"
                    small
                    color="#fef3c7"
                    text-color="#92400e"
                    class="tipo-badge font-weight-bold"
                >
                  <v-icon x-small left color="#92400e">mdi-badge-account</v-icon>
                  Colaborador
                </v-chip>
                <v-chip
                    small
                    :color="clienteSelecionado.tipofisicajuridica === 'F' ? '#dbeafe' : '#d1fae5'"
                    :text-color="clienteSelecionado.tipofisicajuridica === 'F' ? '#1e40af' : '#065f46'"
                    class="tipo-badge"
                >
                  <v-icon
                      x-small
                      left
                      :color="clienteSelecionado.tipofisicajuridica === 'F' ? '#1e40af' : '#065f46'"
                  >
                    {{ clienteSelecionado.tipofisicajuridica === 'F' ? 'mdi-account' : 'mdi-domain' }}
                  </v-icon>
                  {{ clienteSelecionado.tipofisicajuridica === 'F' ? 'Pessoa Física' : 'Pessoa Jurídica' }}
                </v-chip>
              </div>
            </div>

            <v-divider class="mb-3"></v-divider>

            <!-- Grid de Informações -->
            <v-row dense>
              <!-- Nome/Razão Social -->
              <v-col cols="12" sm="8" md="9">
                <div class="info-card-principal mb-3">
                  <div class="info-icon-wrapper">
                    <v-icon color="#023449" size="24">
                      {{ clienteSelecionado.tipofisicajuridica === 'F' ? 'mdi-account-circle' : 'mdi-domain' }}
                    </v-icon>
                  </div>
                  <div class="info-content-principal">
                    <div class="info-label-principal">
                      {{ clienteSelecionado.tipofisicajuridica === 'F' ? 'Nome Completo' : 'Razão Social' }}
                    </div>
                    <div class="info-value-principal">
                      {{ clienteSelecionado.nomeformatado || '-' }}
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- Limite Atual -->
              <v-col cols="12" sm="4" md="3">
                <div class="info-card-principal info-card-limite mb-3">
                  <div class="info-icon-wrapper info-icon-wrapper-limite">
                    <v-icon color="#059669" size="24">mdi-credit-card-check-outline</v-icon>
                  </div>
                  <div class="info-content-principal">
                    <div class="info-label-principal info-label-limite">Limite Atual</div>
                    <div class="info-value-principal info-value-limite monospace">
                      {{ formatarMoedaExibicao(clienteSelecionado.limite) }}
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- CPF/CNPJ -->
              <v-col cols="12" sm="6" md="2">
                <div class="info-card-secundario">
                  <div class="info-header-secundario">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-card-account-details-outline</v-icon>
                    <span class="info-label-secundario">
              {{ clienteSelecionado.tipofisicajuridica === 'F' ? 'CPF' : 'CNPJ' }}
            </span>
                  </div>
                  <div class="info-value-secundario monospace">
                    {{ formatarCpfCnpj(clienteSelecionado.cpfcnpj) }}
                  </div>
                </div>
              </v-col>

              <!-- Telefone -->
              <v-col cols="12" sm="6" md="2">
                <div class="info-card-secundario">
                  <div class="info-header-secundario">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-phone</v-icon>
                    <span class="info-label-secundario">Telefone</span>
                  </div>
                  <div class="info-value-secundario monospace">
                    {{ getTelefone() }}
                  </div>
                </div>
              </v-col>

              <!-- CEP -->
              <v-col cols="12" sm="6" md="2">
                <div class="info-card-secundario">
                  <div class="info-header-secundario">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-map-marker</v-icon>
                    <span class="info-label-secundario">CEP</span>
                  </div>
                  <div class="info-value-secundario monospace">
                    {{ clienteSelecionado.endereco ? formatarCEP(clienteSelecionado.endereco.cep) : '-' }}
                  </div>
                </div>
              </v-col>

              <!-- Cidade/UF -->
              <v-col cols="12" sm="6" md="6">
                <div class="info-card-secundario">
                  <div class="info-header-secundario">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-city</v-icon>
                    <span class="info-label-secundario">Cidade/UF</span>
                  </div>
                  <div class="info-value-secundario">
                    {{ getCidadeUF() }}
                  </div>
                </div>
              </v-col>

              <!-- Endereço Completo -->
              <v-col cols="12">
                <div class="info-card-secundario">
                  <div class="info-header-secundario">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-home-map-marker</v-icon>
                    <span class="info-label-secundario">Endereço Completo</span>
                  </div>
                  <div class="info-value-secundario">
                    {{ getEnderecoCompleto() }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-form>
    </v-card-text>

    <!-- Botão de Ação -->
    <div v-if="mostrarFormulario">
      <v-divider></v-divider>
      <v-card-actions class="pa-3 action-footer">
        <v-btn
            text
            small
            @click="cancelarConsulta"
            :disabled="loading"
        >
          <v-icon left small>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
            dark
            class="action-btn primary elevation-1"
            :disabled="!valid || loadingClientes || loadingClienteInfo"
            @click="dialogConfirmacao = true"
        >
          <v-icon left small>mdi-magnify</v-icon>
          Consultar Limite
        </v-btn>
      </v-card-actions>
    </div>

    <!-- Dialog de Confirmação -->
    <v-dialog v-model="dialogConfirmacao" max-width="600" persistent>
      <v-card class="dialog-confirmacao">
        <!-- Header do Dialog -->
        <v-card-title class="dialog-header pa-4">
          <div class="d-flex align-center">
            <v-avatar color="#10b981" size="48" class="mr-3">
              <v-icon color="white" size="28">mdi-check-circle</v-icon>
            </v-avatar>
            <div>
              <div class="dialog-title">Confirmar Consulta de Crédito</div>
              <div class="dialog-subtitle">Verifique os dados antes de prosseguir</div>
            </div>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Conteúdo do Dialog -->
        <v-card-text class="pa-5">
          <!-- Informações do Cliente -->
          <div class="confirmacao-section mb-4">
            <div class="section-label mb-3">
              <v-icon small color="#64748b" class="mr-2">mdi-account-circle</v-icon>
              Dados do Cliente
            </div>
            <div class="info-card">
              <div class="d-flex align-center">
                <v-icon
                    :color="clienteSelecionado?.tipofisicajuridica === 'F' ? '#3b82f6' : '#059669'"
                    class="mr-3"
                    size="32"
                >
                  {{ clienteSelecionado?.tipofisicajuridica === 'F' ? 'mdi-account' : 'mdi-domain' }}
                </v-icon>
                <div class="flex-grow-1">
                  <div class="cliente-nome-dialog">{{ clienteSelecionado?.nomeformatado }}</div>
                  <div class="cliente-doc-dialog">{{ formatarCpfCnpj(clienteSelecionado?.cpfcnpj) }}</div>
                </div>
                <v-chip
                    x-small
                    :color="clienteSelecionado?.tipofisicajuridica === 'F' ? '#dbeafe' : '#d1fae5'"
                    :text-color="clienteSelecionado?.tipofisicajuridica === 'F' ? '#1e40af' : '#065f46'"
                >
                  {{ clienteSelecionado?.tipofisicajuridica === 'F' ? 'PF' : 'PJ' }}
                </v-chip>
              </div>
            </div>

            <!-- Limite Atual — fora do info-card, com cor condicional -->
            <div class="limite-atual-dialog" :class="limiteDialogClasse">
              <div class="limite-atual-label">
                <v-icon x-small :color="limiteDialogIconeCor" class="mr-1">
                  {{ limiteDialogIcone }}
                </v-icon>
                Limite atual cadastrado
              </div>
              <div class="limite-atual-valor" :class="limiteDialogValorClasse">
                {{ formatarMoedaExibicao(clienteSelecionado?.limite) }}
              </div>
            </div>
          </div>

          <!-- Valor da Consulta -->
          <div class="confirmacao-section">
            <div class="section-label mb-3">
              <v-icon small color="#64748b" class="mr-2">mdi-cash-multiple</v-icon>
              Limite a Consultar
            </div>
            <div class="valor-card" :class="valorCardClasse">
              <div class="valor-label" :class="valorLabelClasse">Limite de Crédito Solicitado</div>
              <div class="valor-principal" :class="valorPrincipalClasse">{{ formatarMoedaExibicao(valorVenda) }}</div>
            </div>
          </div>

          <!-- Alerta de Bloqueio 30 dias -->
          <v-alert
              v-if="erroBloqueio"
              dense
              text
              type="warning"
              icon="mdi-clock-alert-outline"
              class="mt-4 mb-0"
          >
            <div class="alerta-texto">{{ erroBloqueio }}</div>
          </v-alert>

          <!-- Alerta Informativo -->
          <v-alert
              v-else
              dense
              text
              color="#0891b2"
              icon="mdi-information-outline"
              class="mt-4 mb-0"
          >
            <div class="alerta-texto">
              A consulta será realizada através da <strong>Sorter Curve</strong> e pode levar alguns segundos para ser processada.
            </div>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Ações do Dialog -->
        <v-card-actions class="pa-4">
          <v-btn
              text
              large
              @click="dialogConfirmacao = false"
              :disabled="loading"
          >
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
              large
              :class="!limiteAcimaDaVenda ? 'confirmar-btn elevation-2 success' : 'disabled-confirmar-btn'"
              :loading="loading"
              :disabled="limiteAcimaDaVenda || !!erroBloqueio"
              @click="confirmarConsulta"
          >
            <v-icon left>mdi-check-bold</v-icon>
            Confirmar Consulta
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'SorterCurveFormulario',

  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      valid: false,
      clienteSelecionado: null,
      clientes: [],
      searchCliente: null,
      loadingClientes: false,
      loadingClienteInfo: false,
      valorVenda: '',
      debounceTimer: null,
      buscaAtualId: 0,
      dialogConfirmacao: false,
      mostrarFormulario: false,
      erroBloqueio: null,
    };
  },

  watch: {
    searchCliente(val) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      if (!val || val.length < 2) {
        this.clientes = [];
        this.loadingClientes = false;
        this.buscaAtualId++;
        return;
      }

      this.loadingClientes = true;

      this.debounceTimer = setTimeout(() => {
        this.$emit('buscar-clientes', val, this.buscaAtualId);
      }, 1000);
    },

    clienteSelecionado(val) {
      if (val) {
        this.erroBloqueio = null;
        this.loadingClienteInfo = true;
        setTimeout(() => {
          this.loadingClienteInfo = false;
        }, 500);
      }
    }
  },

  computed: {
    // Converte valorVenda (string formatada "1.500,00") para número
    valorVendaNumerico() {
      if (!this.valorVenda) return 0;
      return parseFloat(this.valorVenda.replace('.', '').replace(',', '.')) || 0;
    },

    // Converte limite do cliente para número
    limiteAtualNumerico() {
      const v = this.clienteSelecionado?.limite;
      if (v === null || v === undefined || v === '') return 0;
      return typeof v === 'string'
          ? parseFloat(v.replace('.', '').replace(',', '.')) || 0
          : parseFloat(v) || 0;
    },

    // true = limite cobre o valor solicitado
    limiteAcimaDaVenda() {
      return this.limiteAtualNumerico >= this.valorVendaNumerico;
    },

    limiteDialogClasse() {
      return this.limiteAcimaDaVenda
          ? 'limite-atual-dialog--verde'
          : 'limite-atual-dialog--vermelho';
    },

    limiteDialogValorClasse() {
      return this.limiteAcimaDaVenda
          ? 'limite-atual-valor--verde'
          : 'limite-atual-valor--vermelho';
    },

    limiteDialogIcone() {
      return this.limiteAcimaDaVenda
          ? 'mdi-credit-card-check-outline'
          : 'mdi-credit-card-remove-outline';
    },

    limiteDialogIconeCor() {
      return this.limiteAcimaDaVenda ? '#059669' : '#dc2626';
    },

    // Valor da Consulta — sempre oposto ao Limite Atual:
    // se limite cobre (verde), valor fica vermelho; se não cobre (vermelho), valor fica verde
    valorCardClasse() {
      return this.limiteAcimaDaVenda
          ? 'valor-card--vermelho'
          : 'valor-card--verde';
    },

    valorLabelClasse() {
      return this.limiteAcimaDaVenda
          ? 'valor-label--vermelho'
          : 'valor-label--verde';
    },

    valorPrincipalClasse() {
      return this.limiteAcimaDaVenda
          ? 'valor-principal--vermelho'
          : 'valor-principal--verde';
    },
  },

  methods: {
    cancelarConsulta() {
      this.mostrarFormulario = false;
      this.limparFormulario();
    },

    mostrarErroBloqueio(mensagem) {
      this.erroBloqueio = mensagem;
    },

    setClientes(clientes, buscaId) {
      if (buscaId === this.buscaAtualId) {
        this.clientes = clientes;
        this.loadingClientes = false;
      }
    },

    onClienteChange() {
      if (!this.clienteSelecionado) {
        this.$refs.form.resetValidation();
      }
    },

    confirmarConsulta() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.$emit('criar-analise', {
        idclifor:    this.clienteSelecionado.idcliente,
        valor_venda: parseFloat(this.valorVenda.replace('.', '').replace(',', '.')),
      });
    },

    limparFormulario() {
      this.clienteSelecionado = null;
      this.valorVenda = '';
      this.searchCliente = null;
      this.dialogConfirmacao = false;
      this.loadingClienteInfo = false;
      this.erroBloqueio = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    // ── Helpers de endereço / contato ────────────────────────

    getEnderecoCompleto() {
      if (!this.clienteSelecionado || !this.clienteSelecionado.endereco) return '-';
      const end = this.clienteSelecionado.endereco;
      const partes = [];
      if (end.endereco) partes.push(end.endereco);
      if (end.numero)   partes.push(`nº ${end.numero}`);
      if (end.bairro)   partes.push(end.bairro);
      return partes.join(', ') || '-';
    },

    getCidadeUF() {
      if (!this.clienteSelecionado || !this.clienteSelecionado.endereco) return '-';
      const end = this.clienteSelecionado.endereco;
      const partes = [];
      if (end.cidade) partes.push(end.cidade);
      if (end.uf)     partes.push(end.uf);
      return partes.join(' / ') || '-';
    },

    getTelefone() {
      if (!this.clienteSelecionado || !this.clienteSelecionado.contato) return '-';
      const end = this.clienteSelecionado.contato;
      const telefones = [end.celular, end.telefone2, end.telefonecelular].filter(Boolean);
      if (telefones.length === 0) return '-';
      return this.formatarTelefone(telefones[0]);
    },

    // ── Formatação ───────────────────────────────────────────

    formatarCEP(cep) {
      if (!cep) return '-';
      const numeros = cep.replace(/\D/g, '');
      if (numeros.length === 8) return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
      return cep;
    },

    formatarTelefone(telefone) {
      if (!telefone) return '-';
      const numeros = telefone.replace(/\D/g, '');
      if (numeros.length === 11) return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      if (numeros.length === 10) return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      return telefone;
    },

    formatarCpfCnpj(valor) {
      if (!valor) return '-';
      const numeros = valor.replace(/\D/g, '');
      if (numeros.length === 11) return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      if (numeros.length === 14) return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      return valor;
    },

    formatarMoedaExibicao(valor) {
      if (valor === null || valor === undefined || valor === '') return 'R$ 0,00';
      // Aceita tanto número quanto string formatada (ex.: "1.500,00")
      const numero = typeof valor === 'string'
          ? parseFloat(valor.replace('.', '').replace(',', '.'))
          : parseFloat(valor);
      if (isNaN(numero)) return 'R$ 0,00';
      return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },

    formatarValor(valor) {
      let numeros = valor.replace(/\D/g, '');
      if (numeros) {
        const numero = parseInt(numeros) / 100;
        this.valorVenda = numero.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else {
        this.valorVenda = '';
      }
    },

    somenteNumeros(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    },
  },

  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.buscaAtualId++;
  }
};
</script>

<style scoped>
.form-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
  border: 1px solid #e2e8f0;
}
.form-title { font-size: 1.125rem; font-weight: 700; color: #023449; letter-spacing: -0.01em; }
.form-header { background-color: #fafafa; }
.criar-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #023449 0%, #034d6b 100%) !important;
}

/* Inputs */
.busca-cliente, .valor-input { font-size: 0.938rem; }
::v-deep .busca-cliente .v-input__control,
::v-deep .valor-input .v-input__control { font-size: 0.938rem; }
::v-deep .busca-cliente .v-input__slot,
::v-deep .valor-input .v-input__slot { min-height: 48px; }

/* Lista de Clientes */
.item-nome { font-size: 0.938rem; color: #1e293b; line-height: 1.4; }
.item-info { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.codigo-cliente { color: #64748b; font-weight: 500; }
.separador { color: #cbd5e1; font-weight: 600; }
.documento-cliente { color: #64748b; font-family: 'Roboto Mono', monospace; font-weight: 500; }

/* Preview */
.cliente-preview {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}
.preview-header-container { display: flex; align-items: center; justify-content: space-between; }
.preview-header { display: flex; align-items: center; }
.preview-title { font-size: 0.813rem; font-weight: 700; color: #023449; text-transform: uppercase; letter-spacing: 0.5px; }
.tipo-badge { font-weight: 600; font-size: 0.75rem; letter-spacing: 0.3px; }

/* Card Principal */
.info-card-principal {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #cbd5e1;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
}
.info-card-principal:hover { border-color: #023449; box-shadow: 0 4px 12px rgba(2, 52, 73, 0.08); }
.info-icon-wrapper { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 10px; flex-shrink: 0; }
.info-content-principal { flex: 1; min-width: 0; }
.info-label-principal { font-size: 0.688rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.info-value-principal { font-size: 1.063rem; font-weight: 700; color: #023449; line-height: 1.3; word-break: break-word; }

/* Cards Secundários */
.info-card-secundario {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  height: 100%;
  transition: all 0.2s ease;
}
.info-card-secundario:hover { border-color: #cbd5e1; box-shadow: 0 2px 8px rgba(2, 52, 73, 0.06); }
.info-header-secundario { display: flex; align-items: center; margin-bottom: 6px; }
.info-label-secundario { font-size: 0.688rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.info-value-secundario { font-size: 0.875rem; font-weight: 600; color: #1e293b; line-height: 1.4; word-break: break-word; }
.info-value-secundario.monospace { font-family: 'Roboto Mono', monospace; letter-spacing: -0.02em; }

/* Limite Atual — card irmão do info-card-principal */
.info-card-limite {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%) !important;
  border-color: #a7f3d0 !important;
}
.info-card-limite:hover { border-color: #6ee7b7 !important; box-shadow: 0 4px 12px rgba(5, 150, 105, 0.12) !important; }
.info-icon-wrapper-limite { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%) !important; }
.info-label-limite { color: #065f46 !important; }
.info-value-limite { color: #059669 !important; font-size: 0.938rem !important; }

/* Skeleton */
.skeleton-custom { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
::v-deep .skeleton-custom .v-skeleton-loader__text,
::v-deep .skeleton-custom .v-skeleton-loader__chip { background: transparent !important; }

/* Footer de Ação */
.action-footer { background: #fafafa; }
.action-btn { text-transform: none; font-weight: 600; letter-spacing: 0; border-radius: 8px; padding: 0 12px !important; background: linear-gradient(135deg, #023449 0%, #034d6b 100%) !important; }

/* Dialog de Confirmação */
.dialog-confirmacao { border-radius: 16px !important; overflow: hidden; }
.dialog-header { background: linear-gradient(135deg, #023449 0%, #034d6b 100%); border-bottom: 2px solid #023449; }
.dialog-title { font-size: 1.125rem; font-weight: 700; color: #ffffff; line-height: 1.3; }
.dialog-subtitle { font-size: 0.813rem; color: #e0f2fe; margin-top: 2px; }
::v-deep .dialog-header .v-avatar { background-color: #ffffff !important; }
::v-deep .dialog-header .v-avatar .v-icon { color: #023449 !important; }

/* Seções do Dialog */
.confirmacao-section { margin-bottom: 0; }
.section-label { font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; }
.info-card { background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; padding: 16px; }
.cliente-nome-dialog { font-size: 1rem; font-weight: 700; color: #1e293b; line-height: 1.3; }
.cliente-doc-dialog { font-size: 0.875rem; color: #64748b; font-family: 'Roboto Mono', monospace; margin-top: 4px; }

/* Limite Atual no Dialog */
.limite-atual-dialog {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;
  border: 1px solid;
  transition: all 0.2s ease;
}
.limite-atual-dialog--verde {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #a7f3d0;
}
.limite-atual-dialog--vermelho {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: #fca5a5;
}
.limite-atual-label {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.limite-atual-valor {
  font-size: 0.938rem;
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
  letter-spacing: -0.02em;
}
.limite-atual-valor--verde { color: #059669; }
.limite-atual-valor--vermelho { color: #dc2626; }

/* Valor da Consulta */
.valor-card { border: 2px solid; border-radius: 12px; padding: 20px; text-align: center; transition: all 0.2s ease; }
.valor-card--verde    { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border-color: #10b981; }
.valor-card--vermelho { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); border-color: #f87171; }
.valor-label { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
.valor-label--verde    { color: #065f46; }
.valor-label--vermelho { color: #991b1b; }
.valor-principal { font-size: 2rem; font-weight: 700; font-family: 'Roboto Mono', monospace; letter-spacing: -0.02em; }
.valor-principal--verde    { color: #059669; }
.valor-principal--vermelho { color: #dc2626; }
.alerta-texto { font-size: 0.813rem; color: #0c4a6e; line-height: 1.5; }
.confirmar-btn { text-transform: none; font-weight: 600; letter-spacing: 0; border-radius: 8px; padding: 0 32px !important; background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important; }
.disabled-confirmar-btn { text-transform: none; font-weight: 600; letter-spacing: 0; border-radius: 8px; padding: 0 32px !important; background: linear-gradient(135deg, #c2c2c2 0%, #ababab 100%) !important; }

/* Responsividade */
@media (max-width: 768px) {
  .form-card { border-radius: 8px; }
  .form-title { font-size: 1rem; }
  .criar-btn { font-size: 0.75rem; padding: 0 8px !important; }
  .criar-btn .v-icon { font-size: 16px !important; }
  .cliente-preview { padding: 12px; }
  .preview-header-container { flex-direction: column; align-items: flex-start; gap: 8px; }
  .info-card-principal { flex-direction: column; align-items: flex-start; gap: 12px; }
  .info-icon-wrapper { width: 40px; height: 40px; }
  .info-value-principal { font-size: 0.938rem; }
  .info-card-secundario { padding: 8px 10px; }
  .item-nome { font-size: 0.875rem; }
  .dialog-title { font-size: 1rem; }
  .dialog-subtitle { font-size: 0.75rem; }
  .valor-principal { font-size: 1.5rem; }
  .cliente-nome-dialog { font-size: 0.938rem; }
  .confirmar-btn { width: 100%; margin-top: 8px; }
  .limite-atual-dialog { flex-direction: column; align-items: flex-start; gap: 4px; }
}

@media (max-width: 480px) {
  .item-info { flex-direction: column; align-items: flex-start; gap: 4px; }
  .separador { display: none; }
}
</style>